import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../ui/useMediaQuery.js';
import { clampPaneWidth, readPaneWidth, writePaneWidth } from './width.js';

/** Under this the pane has no room to sit beside the grid, so it covers it. */
export const SHEET_QUERY = '(max-width: 719px)';

export interface DockedPaneProps {
  /** What the pane is about, for a screen reader. */
  label: string;
  onClose: () => void;
  /** To the neighbouring visible row, which is what the arrow keys ask for. A pane with no rows behind it has none. */
  onStep?: (delta: 1 | -1) => void;
  /** A pane of its own kind (the assistant) adds its class to the shared shell. */
  className?: string;
  children: React.ReactNode;
}

/**
 * The shell every record pane shares: docked beside the list rather than over
 * it, so the grid stays live; resizable from its left edge, the width
 * remembered per browser; the arrow keys walk it down the rows and Escape
 * puts it away. Under 720 px it is a full-screen sheet instead, and whatever
 * sits inside says how to get back (`PaneBack`).
 */
export function DockedPane(props: DockedPaneProps): React.JSX.Element {
  const pane = useRef<HTMLElement>(null);
  const sheet = useMediaQuery(SHEET_QUERY);
  const [width, setWidth] = useState(() => readPaneWidth());

  const { onClose, onStep } = props;
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      // A menu, a picklist or a popover answers these keys itself, and says so
      // by cancelling them; a field being typed into owns them outright.
      if (event.defaultPrevented) return;
      const target = event.target;
      if (isTyping(target)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (onStep === undefined) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      // The grid answers its own arrows, and moves the pane as it goes.
      if (!(target instanceof Node) || pane.current?.contains(target) !== true) return;
      event.preventDefault();
      onStep(event.key === 'ArrowDown' ? 1 : -1);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, onStep]);

  const startResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = handle.parentElement?.offsetWidth ?? readPaneWidth();
    handle.setPointerCapture(event.pointerId);

    // Dragging the left edge left makes the pane wider, so the delta is
    // measured the other way round from the pointer's own.
    const onMove = (move: PointerEvent): void =>
      setWidth(clampPaneWidth(startWidth + (startX - move.clientX)));
    const onUp = (): void => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      setWidth((current) => {
        writePaneWidth(current);
        return current;
      });
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
  }, []);

  return (
    <aside
      ref={pane}
      className={[sheet ? 'pane sheet' : 'pane', props.className].filter(Boolean).join(' ')}
      style={sheet ? undefined : { width }}
      aria-label={props.label}
    >
      {!sheet && (
        <div
          className="pane-resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="How wide the pane is"
          onPointerDown={startResize}
        />
      )}
      <div className="pane-body">{props.children}</div>
    </aside>
  );
}

/** Whether the pane is a full-screen sheet right now. */
export function usePaneSheet(): boolean {
  return useMediaQuery(SHEET_QUERY);
}

/** The sheet's way back to the list; nothing at all while the pane is docked. */
export function PaneBack(props: { onClose: () => void }): React.JSX.Element | null {
  const sheet = usePaneSheet();
  if (!sheet) return null;
  return (
    <button className="pane-back" type="button" onClick={props.onClose}>
      ‹ Back
    </button>
  );
}

/** The docked pane's close button; the sheet closes through `PaneBack` instead. */
export function PaneClose(props: { onClose: () => void }): React.JSX.Element | null {
  const sheet = usePaneSheet();
  if (sheet) return null;
  return (
    <button
      className="icon-button"
      type="button"
      aria-label="Close"
      title="Close (Escape)"
      onClick={props.onClose}
    >
      <X aria-hidden="true" />
    </button>
  );
}

/**
 * Whether the keys belong to whatever is under the pointer rather than to the
 * pane: a field being typed into, or a listbox walking its own options.
 */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true;
  if (target.isContentEditable) return true;
  return target.closest('.popover, [role="combobox"], [role="listbox"], [role="menu"]') !== null;
}
